import React, { useCallback, useEffect, useState } from "react";
import DataProvider from "../../apis";
import type { DiagnosticItem } from "../../apis";
import { Card, Tag, Pagination, Spin, Input, Empty, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, UserOutlined, ExperimentOutlined } from "@ant-design/icons";
import Colors from "../../constants/Colors";
import {
  E_PRESCRIPTIONS_BASE,
  diagnosticStatusConfig as statusConfig,
} from "../../constants/e-prescriptions";

export default function Labrequestlist() {
  const [data, setData] = useState<DiagnosticItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(12);
  const [searchName, setSearchName] = useState("");

  const fetchData = useCallback(
    async (page: number = 1) => {
      setLoading(true);
      try {
        const res = await DataProvider.getDiagnosticsQuery(page, pageSize, {
          requestType: "lab",
          ...(searchName && { customerName: searchName }),
        });
        const result = res.data;
        setData(result.diagnostics ?? []);
        setTotalPages(result.totalPages ?? 1);
      } catch (error) {
        console.error("Error fetching lab requests:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, searchName]
  );

  useEffect(() => {
    fetchData(pageNumber);
  }, [fetchData, pageNumber]);

  const handleSearch = () => {
    setPageNumber(1);
    fetchData(1);
  };

  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input.Search
          placeholder="Search by patient name"
          allowClear
          onSearch={handleSearch}
          onChange={(e) => setSearchName(e.target.value)}
          className="max-w-sm"
          aria-label="Search lab requests by patient name"
          prefix={<SearchOutlined className="text-gray-400" />}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" tip="Loading lab requests…" />
        </div>
      ) : data.length > 0 ? (
        <>
          <p className="text-sm text-gray-500 m-0">
            Lab requests are sent to the selected lab for scheduling and sample collection.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 list-none m-0 p-0">
            {data.map((item) => {
              const status = statusConfig[item.status?.toLowerCase()] ?? {
                color: "#94a3b8",
                label: item.status?.replace(/_/g, " ") ?? "—",
              };
              const scheduleDate = item.schedule_date
                ? new Date(item.schedule_date).toLocaleDateString()
                : "—";
              return (
                <li key={item.id}>
                  <Card
                    hoverable
                    onClick={() => navigate(`${E_PRESCRIPTIONS_BASE}/diagnostic/${item.id}`)}
                    className="h-full rounded-lg border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                    styles={{ body: { padding: 16 } }}
                  >
                    <div className="flex gap-4">
                      <div className="shrink-0">
                        <div
                          className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center"
                          aria-hidden
                        >
                          <ExperimentOutlined className="text-2xl text-gray-400" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 m-0 truncate flex items-center gap-1">
                          <UserOutlined className="text-gray-400 text-xs shrink-0" />
                          {item.fullname || "—"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 mb-2 font-mono">
                          Order #{item.orderId}
                        </p>
                        {item.contact && (
                          <p className="text-xs text-gray-500 mt-0 mb-1">{item.contact}</p>
                        )}
                        <Tag color={status.color} className="text-xs rounded">
                          {status.label}
                        </Tag>
                        <p className="text-xs text-gray-400 mt-2 m-0">
                          Scheduled: {scheduleDate}
                          {item.schedule_time ? ` ${item.schedule_time}` : ""}
                        </p>
                      </div>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
          {totalPages > 1 && (
            <div className="flex justify-center pt-4">
              <Pagination
                current={pageNumber}
                total={totalPages * pageSize}
                pageSize={pageSize}
                onChange={(page) => setPageNumber(page)}
                showSizeChanger={false}
                aria-label="Lab requests pagination"
              />
            </div>
          )}
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="text-gray-500">
              No lab requests yet. Create one for a patient and the selected lab will handle scheduling.
            </span>
          }
          className="py-12"
        >
          <Button
            type="primary"
            onClick={() => navigate(`${E_PRESCRIPTIONS_BASE}/labrequest`)}
            style={{ background: Colors.primary }}
            aria-label="Create first lab request"
          >
            Create lab request
          </Button>
        </Empty>
      )}
    </div>
  );
}
